import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  CalendarDays,
  Newspaper,
  MessageSquare,
  Mail,
  Users,
  Handshake,
  HeartHandshake,
  BadgeDollarSign,
  Download,
  LogOut,
  RefreshCw,
  Menu,
  X,
  Images,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  clearAdminSession,
  fetchAdminMe,
  fetchAdminDashboardStats,
  getAdminUser,
} from "../../lib/adminAuth";

const NavItem = ({ to, icon: Icon, label, end = false, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      [
        "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all",
        isActive
          ? "bg-amber-100 text-amber-900"
          : "text-stone-700 hover:bg-stone-100 hover:text-stone-900",
      ].join(" ")
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span>{label}</span>
  </NavLink>
);

const AdminLayout = ({ title = "Admin", children, onRefresh, right }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [me, setMe] = useState(null);
  const adminUser = getAdminUser();

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        const meData = await fetchAdminMe();
        if (mounted) setMe(meData);
      } catch (_) {
        // keep fallback local admin user
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  const logout = () => {
    clearAdminSession();
    navigate("/admin/login", { replace: true });
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;

    try {
      setRefreshing(true);
      await onRefresh();
      await fetchAdminDashboardStats().catch(() => null);
    } finally {
      setRefreshing(false);
    }
  };

  const sidebarSections = useMemo(
    () => [
      {
        title: "Overview",
        items: [
          {
            to: "/admin/dashboard",
            icon: LayoutDashboard,
            label: "Dashboard",
            end: true,
          },
        ],
      },
      {
        title: "Content",
        items: [
          { to: "/admin/projects", icon: FolderKanban, label: "Projects" },
          { to: "/admin/events", icon: CalendarDays, label: "Events" },
          { to: "/admin/posts", icon: Newspaper, label: "Blog Posts" },
          { to: "/admin/gallery", icon: Images, label: "Gallery Manager" },
          { to: "/admin/comments", icon: MessageSquare, label: "Comments" },
          { to: "/admin/tatl", icon: FolderKanban, label: "Manage TATL Stories" },
        ],
      },
      {
        title: "People",
        items: [
          { to: "/admin/contacts", icon: Mail, label: "Contacts" },
          { to: "/admin/volunteers", icon: Users, label: "Volunteers" },
          { to: "/admin/partners", icon: Handshake, label: "Partners" },
          { to: "/admin/donations", icon: HeartHandshake, label: "Donations" },
          { to: "/admin/newsletters", icon: BadgeDollarSign, label: "Newsletter" },
        ],
      },
      {
        title: "Exports",
        items: [
          { to: "/admin/newsletters?export=csv", icon: Download, label: "Download CSV" },
          { to: "/admin/newsletters?export=pdf", icon: Download, label: "Download PDF" },
        ],
      },
    ],
    []
  );

  const sidebar = (
    <div className="h-full flex flex-col">
      <div className="px-4 py-4 border-b border-stone-200">
        <Link to="/admin/dashboard" className="block" onClick={closeMobileSidebar}>
          <h2 className="text-lg font-bold text-stone-900 leading-tight">
            GLGIF Admin
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Content & Operations Console
          </p>
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {sidebarSections.map((section) => (
          <div key={section.title}>
            <p className="px-3 mb-2 text-[11px] uppercase tracking-wider text-stone-400 font-semibold">
              {section.title}
            </p>

            <div className="space-y-1">
              {section.items.map((item) => (
                <NavItem
                  key={item.to + item.label}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  end={item.end}
                  onClick={closeMobileSidebar}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-stone-200 p-3">
        <Button
          variant="outline"
          onClick={logout}
          className="w-full justify-start rounded-xl border-stone-300"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-40 border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden rounded-xl"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-stone-900 truncate">
                {title}
              </h1>
              <p className="text-xs md:text-sm text-stone-600 truncate">
                Welcome, {me?.email || adminUser?.email || "Admin"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {right ? (
              <div className="flex items-center gap-2">{right}</div>
            ) : null}

            {onRefresh && !right ? (
              <Button
                variant="outline"
                className="rounded-xl hidden sm:inline-flex"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
            ) : null}

            <Button
              onClick={logout}
              className="bg-stone-900 hover:bg-stone-800 rounded-xl hidden sm:inline-flex"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeMobileSidebar}
            aria-hidden="true"
          />

          <aside className="absolute left-0 top-0 h-full w-[86%] max-w-[320px] bg-white border-r border-stone-200 shadow-xl">
            <div className="flex items-center justify-between px-3 py-2 border-b border-stone-200">
              <span className="text-sm font-semibold text-stone-700">
                Navigation
              </span>

              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl"
                onClick={closeMobileSidebar}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {sidebar}
          </aside>
        </div>
      )}

      <div className="px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="hidden lg:block h-[calc(100vh-112px)] sticky top-[88px] rounded-2xl border border-stone-200 bg-white overflow-hidden">
          {sidebar}
        </aside>

        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;